import { useRef, useState } from "react";
import {
  Grid,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styles } from "./style/main";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./style/theme";

export default function HeroSection() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const emailRef = useRef();
  const companyRef = useRef();
  const jobRef = useRef();

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault(); // prevent form from auto-submitting
      if (nextRef?.current) {
        nextRef.current.focus(); // go to next field
      } else {
        e.target.form.requestSubmit(); // submit if last field
      }
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
    setErrorOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    setOpen(true);
    setLoading(false);

    fetch(`${process.env.REACT_APP_BASE_URL}`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `firstName=${encodeURIComponent(
        e.target.firstName.value
      )}&lastName=${encodeURIComponent(
        e.target.lastName.value
      )}&email=${encodeURIComponent(
        e.target.email.value
      )}&company=${encodeURIComponent(
        e.target.company.value
      )}&job=${encodeURIComponent(e.target.job.value)}`,
    })
      .then((res) => {
        if (!res.ok)
          throw new Error(`Request failed with status ${res.status}`);
        return res.text();
      })
      .then((data) => console.log("Success:", data))
      .catch((error) => {
        console.error("Error submitting form:", error);
        setErrorOpen(true); // ⚠️ trigger error snackbar
      })
      .finally(() => {
        setLoading(false); // ✅ always stop loading
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        spacing={6}
        direction="row"
        sx={{
          px: { xs: 4, md: 10, lg: 0 },
          pl: { xs: 3, md: 10, lg: 10 },
          py: { xs: 8, md: 12, lg: 10 },
          position: "relative",
          height: { lg: "100vh", xs: "180vh" },
          backgroundColor: "#ffffff",
          overflow: "hidden",
          overflowX: "clip",
          //  overflowY: 'hidden',
        }}
      >
        {/* Left Side */}
        <Grid item xs={12} md={6} lg={6} width="45vw">
          <Grid container direction="column" spacing={{ lg: 4, xs: 0 }}>
            {/* Badge */}
            <Grid item>
              <Typography
                variant="button"
                sx={{
                  color: "#8ac8ff",
                }}
              >
                🚀 Next-Gen Cloud Optimization
              </Typography>
            </Grid>

            {/* Title */}
            <Grid item>
              <Typography
                variant="h2"
                fontWeight="bold"
                sx={{
                  color: "primary.main",
                  lineHeight: "54px",
                }}
              >
                Optimize Your Cloud <br /> Infrastructure
              </Typography>
            </Grid>

            {/* Subtitle */}
            <Grid item>
              <Typography
                sx={{
                  color: "rgb(132, 133, 135)",
                  fontSize: { xs: "0.9rem", lg: "1rem" },
                  maxWidth: "90%",
                }}
              >
                Reduce cloud costs by up to 50% with AI-powered optimization.
                Get complete visibility, automated scaling, and continuous cost
                monitoring across all your cloud resources.
              </Typography>
            </Grid>

            {/* CTA Buttons */}
            <Grid item>
              <Grid container spacing={{ lg: 2, xs: 0 }}>
                <Grid item>
                  <Button
                    variant="contained"
                    size="medium"
                    // sx={{ backgroundColor: "rgb(52, 56, 149) " }}
                  >
                    Start Free Trial
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Form */}
        <Grid item xs={12} md={6} sm={6} lg={7} width="44vw">
          <Grid
            container
            direction="column"
            spacing={1}
            sx={{
              height: { lg: "110%", xs: "80vh" },
              width: { lg: "34vw", xs: "90vw" },
              p: { xs: 3, md: 4, lg: 6 },
              pb: { xs: 3, md: 4, lg: 0 },
              borderRadius: 4,
              mx: { xs: "auto", md: 0 },
              boxShadow: 5,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.02)",
                filter: "brightness(1.05)",
                boxShadow: "0px 12px 24px rgba(94, 94, 94, 0.35)",
              },
            }}
          >
            <Grid item>
              <Typography variant="h6" fontWeight="bold">
                Get Started Today
              </Typography>
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Join thousands of companies optimizing their cloud spend
              </Typography>
            </Grid>
            <form onSubmit={handleSubmit}>
              <Grid item container height="32vh" spacing={1} mt={2}>
                <Grid item xs={6} sx={styles.textfield}>
                  <TextField
                    fullWidth
                    inputRef={firstNameRef}
                    name="firstName"
                    placeholder="First Name *"
                    required
                    variant="standard"
                    onKeyDown={(e) => handleKeyDown(e, lastNameRef)}
                    InputProps={{ disableUnderline: true }}
                  />
                </Grid>

                <Grid item xs={6} sx={styles.textfield}>
                  <TextField
                    fullWidth
                    inputRef={lastNameRef}
                    required
                    placeholder="Last Name *"
                    name="lastName"
                    variant="standard"
                    onKeyDown={(e) => handleKeyDown(e, emailRef)}
                    InputProps={{ disableUnderline: true }}
                  />
                </Grid>

                <Grid item xs={12} sx={styles.textfield}>
                  <TextField
                    fullWidth
                    inputRef={emailRef}
                    placeholder="Business Email *"
                    name="email"
                    type="email"
                    required
                    variant="standard"
                    onKeyDown={(e) => handleKeyDown(e, companyRef)}
                    InputProps={{ disableUnderline: true }}
                  />
                </Grid>

                <Grid item xs={12} sx={styles.textfield}>
                  <TextField
                    fullWidth
                    inputRef={companyRef}
                    placeholder="Company Name *"
                    name="company"
                    required
                    variant="standard"
                    onKeyDown={(e) => handleKeyDown(e, jobRef)}
                    InputProps={{ disableUnderline: true }}
                  />
                </Grid>

                <Grid item xs={12} sx={styles.textfield}>
                  <TextField
                    fullWidth
                    inputRef={jobRef}
                    placeholder="Job Title *"
                    name="job"
                    required
                    variant="standard"
                    onKeyDown={(e) => handleKeyDown(e, null)} // No next field
                    InputProps={{ disableUnderline: true }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} mt={2}>
                <Grid item xs={12} sx={{ width: { lg: "26vw", xs: "50vw" } }}>
                  <Button
                    // onClick={}
                    fullWidth
                    variant="contained"
                    type="submit"
                    size="large"
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: "#888" }}>
                    By submitting, you agree to our{" "}
                    <span
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                      Privacy Policy
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
        <Snackbar
          open={open}
          autoHideDuration={2000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleClose}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor: "rgb(38, 142, 57)",
              borderRadius: "21px",
            }}
          >
            Form submitted successfully!
          </Alert>
        </Snackbar>
        <Snackbar
          open={errorOpen}
          autoHideDuration={2000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleClose}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor: "rgb(212, 51, 51)",
              borderRadius: "21px",
            }}
          >
            Something went wrong!
          </Alert>
        </Snackbar>

        <Grid item xs={12} mt={4} lg={12}>
          <Grid container spacing={4} justifyContent="center">
            {[
              { label: "Average Cost Reduction", value: "50%" },
              { label: "Enterprise Customers", value: "500+" },
              { label: "Savings Generated", value: "$2.5B" },
              { label: "Uptime Guarantee", value: "99.9%" },
            ].map((stat, i) => (
              <Grid key={i} item xs={6} md={3} textAlign="center">
                <Typography variant="h5" fontWeight="bold">
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: "#bbb" }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
